import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { safeSessionStorage } from '@/stores/persistStorage';
import { stripDocumentExtension } from '@/utils/formatters';

export interface PageRangeSegment {
  id: string;
  name: string;
  startPage: number;
  endPage: number;
}

export interface PendingDocument {
  id: string;
  file: File;
  name: string;
  pageCount: number | null;
  segments: PageRangeSegment[];
  selectedPages: number[] | null;
  startPage: number | null;
  endPage: number | null;
}

interface BatchSessionState {
  pendingDocuments: PendingDocument[];
  batchJobIds: string[];
  /** Editable name for single-file (non-batch) uploads. */
  documentName: string | null;
}

interface BatchSessionActions {
  setPendingDocuments: (docs: PendingDocument[]) => void;
  addPendingDocuments: (docs: PendingDocument[]) => void;
  removePendingDocument: (id: string) => void;
  clearPendingDocuments: () => void;
  updateDocument: (
    id: string,
    updates: Partial<
      Pick<
        PendingDocument,
        'name' | 'pageCount' | 'segments' | 'selectedPages' | 'startPage' | 'endPage'
      >
    >
  ) => void;
  addSegment: (docId: string) => void;
  updateSegment: (
    docId: string,
    segId: string,
    updates: Partial<Pick<PageRangeSegment, 'name' | 'startPage' | 'endPage'>>
  ) => void;
  removeSegment: (docId: string, segId: string) => void;
  setDocumentPageRange: (docId: string, startPage: number | null, endPage: number | null) => void;
  setDocumentSelectedPages: (docId: string, selectedPages: number[] | null) => void;
  setDocumentName: (name: string | null) => void;
  startBatchSession: (jobIds: string[]) => void;
  removeBatchJob: (jobId: string) => void;
  clearBatchSession: () => void;
}

type BatchSessionStore = BatchSessionState & BatchSessionActions;

const initialState: BatchSessionState = {
  pendingDocuments: [],
  batchJobIds: [],
  documentName: null,
};

export const useBatchSessionStore = create<BatchSessionStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setPendingDocuments: (docs) =>
          set({ pendingDocuments: docs }, false, 'setPendingDocuments'),

        addPendingDocuments: (docs) =>
          set(
            (state) => ({ pendingDocuments: [...state.pendingDocuments, ...docs] }),
            false,
            'addPendingDocuments'
          ),

        removePendingDocument: (id) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.filter((d) => d.id !== id),
            }),
            false,
            'removePendingDocument'
          ),

        clearPendingDocuments: () => set({ pendingDocuments: [] }, false, 'clearPendingDocuments'),

        updateDocument: (id, updates) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) =>
                d.id === id ? { ...d, ...updates } : d
              ),
            }),
            false,
            'updateDocument'
          ),

        addSegment: (docId) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) => {
                if (d.id !== docId || !d.pageCount) return d;
                const lastEnd =
                  d.segments.length > 0 ? Math.max(...d.segments.map((s) => s.endPage)) : 0;
                const start = Math.min(lastEnd + 1, d.pageCount);
                const baseName = d.name || stripDocumentExtension(d.file.name);
                const partNumber = d.segments.length + 1;
                return {
                  ...d,
                  selectedPages: null,
                  startPage: null,
                  endPage: null,
                  segments: [
                    ...d.segments,
                    {
                      id: crypto.randomUUID(),
                      name: `Part ${partNumber} - ${baseName}`,
                      startPage: start,
                      endPage: d.pageCount,
                    },
                  ],
                };
              }),
            }),
            false,
            'addSegment'
          ),

        updateSegment: (docId, segId, updates) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) =>
                d.id === docId
                  ? {
                      ...d,
                      segments: d.segments.map((s) => (s.id === segId ? { ...s, ...updates } : s)),
                    }
                  : d
              ),
            }),
            false,
            'updateSegment'
          ),

        removeSegment: (docId, segId) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) =>
                d.id === docId ? { ...d, segments: d.segments.filter((s) => s.id !== segId) } : d
              ),
            }),
            false,
            'removeSegment'
          ),

        setDocumentPageRange: (docId, startPage, endPage) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) =>
                d.id === docId ? { ...d, startPage, endPage, segments: [] } : d
              ),
            }),
            false,
            'setDocumentPageRange'
          ),

        setDocumentSelectedPages: (docId, selectedPages) =>
          set(
            (state) => ({
              pendingDocuments: state.pendingDocuments.map((d) =>
                d.id === docId ? { ...d, selectedPages, segments: [] } : d
              ),
            }),
            false,
            'setDocumentSelectedPages'
          ),

        setDocumentName: (name) => set({ documentName: name }, false, 'setDocumentName'),

        startBatchSession: (jobIds) =>
          set(
            (state) => ({
              batchJobIds: [...state.batchJobIds, ...jobIds],
              pendingDocuments: [],
            }),
            false,
            'startBatchSession'
          ),

        // Stops tracking it here; the job itself is untouched and stays under Jobs.
        removeBatchJob: (jobId) =>
          set(
            (state) => ({ batchJobIds: state.batchJobIds.filter((id) => id !== jobId) }),
            false,
            'removeBatchJob'
          ),

        clearBatchSession: () => set(initialState, false, 'clearBatchSession'),
      }),
      {
        name: 'batch-session-storage',
        storage: createJSONStorage(() => safeSessionStorage),
        partialize: (state) => ({ batchJobIds: state.batchJobIds }),
      }
    ),
    { name: 'BatchSessionStore', enabled: import.meta.env.DEV }
  )
);

export const usePendingDocuments = () => useBatchSessionStore((s) => s.pendingDocuments);
export const useBatchJobIds = () => useBatchSessionStore((s) => s.batchJobIds);
export const useIsBatchActive = () => useBatchSessionStore((s) => s.batchJobIds.length > 0);
export const useTotalJobCount = () =>
  useBatchSessionStore((s) =>
    s.pendingDocuments.reduce((sum, doc) => sum + Math.max(doc.segments.length, 1), 0)
  );
export const useDocumentName = () => useBatchSessionStore((s) => s.documentName);
export const useBatchSessionActions = () =>
  useBatchSessionStore(
    useShallow((s) => ({
      setPendingDocuments: s.setPendingDocuments,
      addPendingDocuments: s.addPendingDocuments,
      removePendingDocument: s.removePendingDocument,
      clearPendingDocuments: s.clearPendingDocuments,
      updateDocument: s.updateDocument,
      addSegment: s.addSegment,
      updateSegment: s.updateSegment,
      removeSegment: s.removeSegment,
      setDocumentPageRange: s.setDocumentPageRange,
      setDocumentSelectedPages: s.setDocumentSelectedPages,
      setDocumentName: s.setDocumentName,
      startBatchSession: s.startBatchSession,
      removeBatchJob: s.removeBatchJob,
      clearBatchSession: s.clearBatchSession,
    }))
  );
